import React, { useState, useEffect, useCallback } from 'react';
import { Search, Calendar, Filter, ChevronLeft, ChevronRight, RefreshCw, Printer, Download } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import axios from 'axios';
import { printClientReceipt } from '../../components/PrintTicket';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_MAP = {
  received: { label: 'Recue', color: '#E9C46A' },
  preparing: { label: 'En cours', color: '#E16833' },
  ready: { label: 'Prete', color: '#2D6A4F' },
  served: { label: 'Servie', color: '#0A422D' },
};

const SOURCE_MAP = {
  dine_in: 'Sur place', takeaway: 'Emporter', click_collect: 'Click & Collect',
  grab: 'Grab', foodpanda: 'Foodpanda', uber_eats: 'Uber Eats', deliveroo: 'Deliveroo',
};

export const OrderHistoryTab = ({ formatPrice }) => {
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const limit = 20;

  // Filters
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [filterTable, setFilterTable] = useState('');
  const [filterServer, setFilterServer] = useState('');
  const [filterSource, setFilterSource] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPaid, setFilterPaid] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('limit', limit);
      params.set('skip', page * limit);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      if (filterTable) params.set('table_id', filterTable);
      if (filterServer) params.set('server_name', filterServer);
      if (filterSource !== 'all') params.set('source', filterSource);
      if (filterStatus !== 'all') params.set('status', filterStatus);
      if (filterPaid !== 'all') params.set('is_paid', filterPaid);
      if (searchQuery) params.set('search', searchQuery);

      const res = await axios.get(`${API}/admin/orders/history?${params.toString()}`);
      setOrders(res.data.orders);
      setTotal(res.data.total);
    } catch {} finally { setLoading(false); }
  }, [page, dateFrom, dateTo, filterTable, filterServer, filterSource, filterStatus, filterPaid, searchQuery]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const resetFilters = () => {
    setDateFrom(''); setDateTo(''); setFilterTable(''); setFilterServer('');
    setFilterSource('all'); setFilterStatus('all'); setFilterPaid('all');
    setSearchQuery(''); setPage(0);
  };

  const totalPages = Math.ceil(total / limit);
  const fp = formatPrice || ((p) => `฿${(p || 0).toLocaleString()}`);

  return (
    <div className="space-y-4" data-testid="order-history-tab">
      {/* Filters bar */}
      <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3] space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-[#2D241D] flex items-center gap-2">
            <Search className="w-5 h-5 text-[#E16833]" /> Historique des commandes
            <span className="text-sm font-normal text-[#6E5E52]">({total} resultats)</span>
          </h3>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={resetFilters} className="rounded-xl text-xs" data-testid="reset-filters">
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Reset
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
          <div>
            <label className="text-[10px] text-[#6E5E52] font-medium block mb-0.5">Date debut</label>
            <Input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(0); }}
              className="h-8 text-xs rounded-lg border-[#E5DCD3]" data-testid="filter-date-from" />
          </div>
          <div>
            <label className="text-[10px] text-[#6E5E52] font-medium block mb-0.5">Date fin</label>
            <Input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(0); }}
              className="h-8 text-xs rounded-lg border-[#E5DCD3]" data-testid="filter-date-to" />
          </div>
          <div>
            <label className="text-[10px] text-[#6E5E52] font-medium block mb-0.5">Table</label>
            <Input value={filterTable} onChange={e => { setFilterTable(e.target.value); setPage(0); }}
              placeholder="N° table" className="h-8 text-xs rounded-lg border-[#E5DCD3]" data-testid="filter-table" />
          </div>
          <div>
            <label className="text-[10px] text-[#6E5E52] font-medium block mb-0.5">Serveur</label>
            <Input value={filterServer} onChange={e => { setFilterServer(e.target.value); setPage(0); }}
              placeholder="Nom" className="h-8 text-xs rounded-lg border-[#E5DCD3]" data-testid="filter-server" />
          </div>
          <div>
            <label className="text-[10px] text-[#6E5E52] font-medium block mb-0.5">Source</label>
            <select value={filterSource} onChange={e => { setFilterSource(e.target.value); setPage(0); }}
              className="h-8 w-full text-xs rounded-lg border border-[#E5DCD3] px-2 bg-white" data-testid="filter-source">
              <option value="all">Toutes</option>
              <option value="dine_in">Sur place</option>
              <option value="takeaway">Emporter</option>
              <option value="click_collect">Click & Collect</option>
              <option value="grab">Grab</option>
              <option value="foodpanda">Foodpanda</option>
              <option value="uber_eats">Uber Eats</option>
              <option value="deliveroo">Deliveroo</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] text-[#6E5E52] font-medium block mb-0.5">Statut</label>
            <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(0); }}
              className="h-8 w-full text-xs rounded-lg border border-[#E5DCD3] px-2 bg-white" data-testid="filter-status">
              <option value="all">Tous</option>
              <option value="received">Recue</option>
              <option value="preparing">En cours</option>
              <option value="ready">Prete</option>
              <option value="served">Servie</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] text-[#6E5E52] font-medium block mb-0.5">Paiement</label>
            <select value={filterPaid} onChange={e => { setFilterPaid(e.target.value); setPage(0); }}
              className="h-8 w-full text-xs rounded-lg border border-[#E5DCD3] px-2 bg-white" data-testid="filter-paid">
              <option value="all">Tous</option>
              <option value="true">Paye</option>
              <option value="false">Non paye</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] text-[#6E5E52] font-medium block mb-0.5">Recherche</label>
            <Input value={searchQuery} onChange={e => { setSearchQuery(e.target.value); setPage(0); }}
              placeholder="#numero" className="h-8 text-xs rounded-lg border-[#E5DCD3]" data-testid="filter-search" />
          </div>
        </div>
      </div>

      {/* Orders table */}
      <div className="bg-white rounded-2xl border border-[#E5DCD3] overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-6 h-6 animate-spin text-[#6E5E52]" />
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-12 text-[#6E5E52]">
            <Search className="w-10 h-10 mx-auto mb-2 opacity-20" />
            <p className="font-medium">Aucune commande trouvee</p>
          </div>
        ) : (
          <div className="divide-y divide-[#F3EDE5]">
            {orders.map(order => {
              const src = order.order_source || 'dine_in';
              const isExpanded = expandedOrder === order.id;
              const isPaid = order.is_fully_paid;
              const st = STATUS_MAP[order.status] || { label: order.status, color: '#888' };
              return (
                <div key={order.id} data-testid={`history-order-${order.id}`}>
                  <div className="px-4 py-3 flex items-center gap-3 cursor-pointer hover:bg-[#FAF7F2] transition-colors"
                    onClick={() => setExpandedOrder(isExpanded ? null : order.id)}>
                    <span className="text-[10px] px-2 py-0.5 rounded-lg font-bold text-white" style={{ backgroundColor: st.color }}>
                      {st.label}
                    </span>
                    <span className="text-xs font-mono text-[#6E5E52]">{order.order_number || '-'}</span>
                    <span className="text-xs font-bold text-[#2D241D]">
                      {order.is_takeaway ? (src === 'click_collect' ? 'C&C' : 'Emporter') : `T${order.table_id}`}
                    </span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#F3EDE5] text-[#6E5E52] font-medium">
                      {SOURCE_MAP[src] || src}
                    </span>
                    {order.server_name && <span className="text-[10px] text-[#6E5E52]">{order.server_name}</span>}
                    <span className="flex-1" />
                    <span className="text-sm font-bold text-[#0A422D]">{fp(order.total)}</span>
                    {isPaid ? (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-bold">Paye</span>
                    ) : (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-600 font-bold">Non paye</span>
                    )}
                    <span className="text-[10px] text-[#6E5E52]">
                      {order.created_at ? new Date(order.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
                    </span>
                  </div>
                  {isExpanded && (
                    <div className="px-6 py-3 bg-[#FAF7F2] border-t border-[#E5DCD3]">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-[10px] text-[#6E5E52] font-bold mb-1">ARTICLES</p>
                          {(order.items || []).map((item, idx) => (
                            <div key={idx} className="flex justify-between text-xs py-0.5">
                              <span className="text-[#2D241D]">{item.quantity}x {item.name}</span>
                              <span className="font-bold text-[#6E5E52]">{fp(item.price * item.quantity)}</span>
                            </div>
                          ))}
                          {order.notes && <p className="text-[10px] text-[#E16833] italic mt-1">Note: {order.notes}</p>}
                        </div>
                        <div className="space-y-1">
                          <p className="text-[10px] text-[#6E5E52] font-bold mb-1">DETAILS</p>
                          <p className="text-xs">Total: <span className="font-bold">{fp(order.total)}</span></p>
                          {order.discount_amount > 0 && <p className="text-xs">Remise: <span className="text-red-500">-{fp(order.discount_amount)}</span></p>}
                          {order.commission_amount > 0 && <p className="text-xs">Commission: <span className="text-[#E16833]">{fp(order.commission_amount)}</span></p>}
                          {order.tip_amount > 0 && <p className="text-xs">Pourboire: <span className="text-green-600">+{fp(order.tip_amount)}</span></p>}
                          <p className="text-xs">Cree: {order.created_at ? new Date(order.created_at).toLocaleString('fr-FR') : '-'}</p>
                          <button onClick={(e) => { e.stopPropagation(); printClientReceipt(order); }}
                            className="mt-2 flex items-center gap-1 text-xs text-[#0A422D] hover:underline font-medium"
                            data-testid={`print-history-${order.id}`}>
                            <Printer className="w-3 h-3" /> Imprimer ticket
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
            className="rounded-xl" data-testid="history-prev">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm text-[#6E5E52] font-medium">
            Page {page + 1} / {totalPages}
          </span>
          <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
            className="rounded-xl" data-testid="history-next">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
};
